/* Личная раскладка таблицы: ширина столбцов, их порядок и состав.
 *
 * Ширина, порядок и состав — настройка рабочего места, а не данные проекта: на
 * сервер они не носятся и живут в localStorage ключом на таблицу. Таблица без
 * имени (`name`) раскладку не запоминает — это осознанно: без имени два разных
 * реестра писали бы друг другу поверх.
 *
 * Столбцы, которых нет в сохранённом порядке, встают в конец в исходном
 * порядке описания: набор колонок меняется от сборки к сборке, и новая колонка
 * обязана появиться, а не пропасть из-за старой записи в хранилище.
 */
import { useCallback, useState } from 'react'

export interface TableLayout {
  widths: Record<string, number>
  order: string[]
  /* Столбцы, которые человек убрал с экрана. Реестру нужно 1300 px, а на
     ноутбуке под таблицу остаётся 900: колонки прячутся вручную и набор
     запоминается. Скрытых нет по умолчанию — решает пользователь, а не ширина
     окна. */
  hidden: string[]
}

const STORE_KEY = 'tdms_table_layout_v1'
/** Уже столбца текст не читается, шире — колонка выдавливает соседей за экран. */
export const MIN_COL = 56
export const MAX_COL = 720

function readAll(): Record<string, TableLayout> {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    return raw ? (JSON.parse(raw) as Record<string, TableLayout>) : {}
  } catch {
    return {}
  }
}

function remember(name: string, layout: TableLayout) {
  try {
    const all = readAll()
    all[name] = layout
    localStorage.setItem(STORE_KEY, JSON.stringify(all))
  } catch {
    /* Приватный режим: раскладка не переживёт вкладку, ронять таблицу незачем. */
  }
}

/** Сохранённый порядок, дополненный новыми столбцами и очищенный от исчезнувших. */
export function applyOrder<C extends { key: string }>(columns: C[], order: string[]): C[] {
  if (!order.length) return columns
  const byKey = new Map(columns.map((c) => [c.key, c]))
  const known = order.map((k) => byKey.get(k)).filter((c): c is C => c != null)
  const rest = columns.filter((c) => !order.includes(c.key))
  return [...known, ...rest]
}

/**
 * Видимые столбцы. Ключевой (`required`) остаётся всегда: запись без номера
 * документа не опознать, а запись в хранилище переживает сборку, в которой
 * столбец ещё скрывался.
 */
export function applyHidden<C extends { key: string; required?: boolean }>(columns: C[], hidden: string[]): C[] {
  if (!hidden.length) return columns
  return columns.filter((c) => c.required || !hidden.includes(c.key))
}

/**
 * Порядок после переноса столбца `from` на место столбца `to`.
 *
 * Направление переноса решает, с какой стороны от цели встанет столбец: тянули
 * слева направо — встаёт за целью, справа налево — перед ней. Иначе столбец
 * невозможно сделать последним: он всегда вставал бы перед крайним.
 */
export function moveColumn(order: string[], from: string, to: string): string[] {
  if (from === to) return order
  const fromAt = order.indexOf(from)
  const toAt = order.indexOf(to)
  if (fromAt < 0 || toAt < 0) return order
  const next = order.filter((k) => k !== from)
  const at = next.indexOf(to)
  next.splice(fromAt < toAt ? at + 1 : at, 0, from)
  return next
}

/**
 * Сохранённый порядок, дополненный текущими ключами.
 *
 * <p>Ровно то, что показывает {@link applyOrder}: сперва известные столбцы в
 * своём порядке, следом появившиеся позже. Исчезнувшие из кода отбрасываются —
 * переносить их некуда.
 */
export function mergeOrder(order: string[], columnKeys: string[]): string[] {
  const known = order.filter((k) => columnKeys.includes(k))
  return [...known, ...columnKeys.filter((k) => !known.includes(k))]
}

export function clampCol(width: number) {
  return Math.min(Math.max(Math.round(width), MIN_COL), MAX_COL)
}

export function useTableLayout(name: string | undefined, columnKeys: string[]) {
  const [layout, setLayout] = useState<TableLayout>(() => {
    /* Запись в хранилище старше кода: у раскладок, сохранённых до появления
       скрытых столбцов, поля `hidden` нет, и обращение к нему было бы
       обращением к undefined. */
    const saved = name ? readAll()[name] : undefined
    return { widths: saved?.widths ?? {}, order: saved?.order ?? [], hidden: saved?.hidden ?? [] }
  })
  /* Тяга за границу заголовка: отсчёт от фактической ширины ячейки, а не от
     числа в описании столбца — оно задано не у всех, и первый же пиксель
     схлопнул бы такой столбец до минимума.
     `seed` — фактические ширины всех столбцов на момент начала тяги. Они нужны
     потому, что тянуть можно только в фиксированном раскладе таблицы: в
     `table-layout: auto` ширина ячейки — пожелание, и браузер его не исполняет.
     Переключение на фиксированный расклад без снятых ширин перекроило бы всю
     таблицу от первого же движения мыши. */
  const resize = useCallback(
    (key: string, startWidth: number, dx: number, done: boolean, seed?: Record<string, number>) => {
      setLayout((prev) => {
        const widths = { ...seed, ...prev.widths, [key]: clampCol(startWidth + dx) }
        const next = { ...prev, widths }
        if (done && name) remember(name, next)
        return next
      })
    },
    [name],
  )

  const reorder = useCallback(
    (from: string, to: string) => {
      setLayout((prev) => {
        /* Пустой порядок означает «как описано»; чтобы переставлять, его сперва
           нужно материализовать — иначе переносить нечего.

           Сохранённый порядок ДОПОЛНЯЕТСЯ текущими ключами, а не берётся как
           есть: набор колонок растёт от сборки к сборке, а запись в хранилище
           старше кода. Колонки, которой в записи нет («Титул», «Объект»,
           проектный атрибут), moveColumn не находил и молча возвращал порядок
           без изменений — новая колонка не переносилась вовсе, и выглядело это
           как сломанное перетаскивание. Порядок тот же, что показывает
           applyOrder: сперва известные, следом остальные. */
        const base = mergeOrder(prev.order, columnKeys)
        const next = { ...prev, order: moveColumn(base, from, to) }
        if (name) remember(name, next)
        return next
      })
    },
    [name, columnKeys],
  )

  const toggleColumn = useCallback((key: string) => {
    setLayout((prev) => {
      const hidden = prev.hidden.includes(key) ? prev.hidden.filter((k) => k !== key) : [...prev.hidden, key]
      const next = { ...prev, hidden }
      if (name) remember(name, next)
      return next
    })
  }, [name])

  const reset = useCallback(() => {
    const empty = { widths: {}, order: [], hidden: [] }
    setLayout(empty)
    if (name) remember(name, empty)
  }, [name])

  return { layout, resize, reorder, reset, toggleColumn }
}
